import { allowanceRulesQuery, unsupportedMembersQuery } from "@/api/allowance";
import { supporterInvoicesQuery } from "@/api/invoice";
import { supporterLegalTermsListQuery } from "@/api/orgLegalTerms";
import {
  membersQuery,
  supporterBillingDetailsQuery,
  supporterQuery,
  supporterSettingQuery,
  supporterStaffQuery,
  supporterWalletsQuery,
  useSetSupporterSettingMutation,
} from "@/api/supporter";
import { supporterCountriesQuery } from "@/api/supporterCountries";
import { allEapSessionRequestsQuery, isAnyCountryEapEnabled, supporterEAPSettingsQuery } from "@/api/supporterEap";
import { EmptyState } from "@/components/ui/EmptyState";
import { PagePending } from "@/components/ui/PagePending";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { RichCard } from "@/components/ui/RichCard";
import { useSupporterMoneyFormatters } from "@/hooks/usePreferredCurrencyState";
import { useSupporterBenefits } from "@/hooks/useSupporterBenefits";
import { countryCodeToTokenCurrencyCode, tokenCurrencyToMoneyCurrency } from "@/modules/money-fns";
import { formatDate } from "@common/utils/date";
import { formatDollars, formatWholeNumber } from "@common/utils/i18n";
import { Box, Flex, Select, Span, Text } from "@givenwell/components";
import {
  BagDollarIcon,
  CaretRightIcon,
  CheckCircleIcon,
  CreditCardIcon,
  HealthcareIcon,
  UserIcon,
} from "@givenwell/core-solid-icons";
import { styled } from "@givenwell/stitches";
import { colors, fonts } from "@givenwell/theme";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, createLink } from "@tanstack/react-router";
import { ComponentProps, ReactNode } from "react";
import { fallback, object, optional, string } from "valibot";
import { CreateAllowanceRuleModal } from "./allowances/CreateAllowanceRuleDialog";
import { AddTeamMemberModal } from "./team/AddTeamMemberModal";

export const Route = createFileRoute("/supporter/$supporterId/")({
  pendingComponent: PagePending,
  validateSearch: object({
    currency: fallback(optional(string()), undefined),
  }),
  component: SupporterHome,
});

function SupporterHome() {
  const { supporterId } = Route.useParams();
  const { currency } = Route.useSearch();
  const { data: supporter } = useSuspenseQuery(supporterQuery(supporterId));
  const { data: wallets } = useSuspenseQuery(supporterWalletsQuery(supporterId));
  const navigate = Route.useNavigate();

  return (
    <PageWrapper
      maxWidth={1400}
      title="Dashboard"
      rightContent={
        <>
          {wallets.wallets.length > 1 && (
            <Select
              value={currency || countryCodeToTokenCurrencyCode(supporter.country_code)}
              onValueChange={currency => {
                navigate({
                  to: ".",
                  search: {
                    currency: currency,
                  },
                });
              }}
              css={{ width: 100 }}
            >
              {wallets.wallets.map(wallet => (
                <option key={wallet.currency} value={wallet.currency}>
                  {tokenCurrencyToMoneyCurrency(wallet.currency)}
                </option>
              ))}
            </Select>
          )}
        </>
      }
    >
      <Box css={{ spaceY: 16, flex: "2 0 0px" }}>
        <AtAGlanceCard />
        <TodoList />
      </Box>
    </PageWrapper>
  );
}

const relativeFmt = new Intl.RelativeTimeFormat("en-NZ", {});

function formatRelativeTime(millis: number) {
  // const sign = millis >= 0 ? 1 : -1;
  // millis = Math.abs(millis);
  const abs = Math.abs;
  const round = Math.trunc;

  const seconds = millis / 1000;
  const minutes = seconds / 60;
  const hours = minutes / 60;
  const days = hours / 24;
  const weeks = days / 7;
  const months = days / 30;

  if (abs(seconds) < 60) {
    return relativeFmt.format(round(seconds), "second");
  }
  if (abs(minutes) < 60) {
    return relativeFmt.format(round(minutes), "minute");
  }
  if (abs(hours) < 24) {
    return relativeFmt.format(round(hours), "hour");
  }
  if (abs(days) < 14) {
    return relativeFmt.format(round(days), "day");
  }
  if (abs(weeks) < 4) {
    return relativeFmt.format(round(weeks), "week");
  }

  return relativeFmt.format(round(months), "month");
}

function AtAGlanceCard() {
  const { supporterId } = Route.useParams();
  const { data: members } = useSuspenseQuery(membersQuery(supporterId));
  const { data: unsupportedMembers } = useSuspenseQuery(unsupportedMembersQuery(supporterId));
  const benefits = useSupporterBenefits(supporterId);

  const totalCurrentEmployees = members?.members.length;
  // Invited but not yet activated. Derived from aggregate counts rather than per-member status,
  // which is no longer exposed to supporters (it would reveal who has registered).
  const notSignedUpCount = members.invited_count - members.activated_count;

  let { currency } = Route.useSearch();
  const { data: supporter } = useSuspenseQuery(supporterQuery(supporterId));
  currency = currency ?? countryCodeToTokenCurrencyCode(supporter.country_code)!;

  const { data: wallets } = useSuspenseQuery(supporterWalletsQuery(supporterId));
  const wallet = wallets.wallets.find(w => w.currency === currency)!;

  const format = useSupporterMoneyFormatters();
  const { data: eapData } = useQuery(supporterEAPSettingsQuery(supporterId));

  return (
    <Box
      css={{
        display: "grid",
        gap: 1,
        gridCols: 1,
        backgroundColor: colors.outline2,
        boxShadow: `0 0 0 1px ${colors.outline2}`,
        borderRadius: 6 + 20,
        overflow: "hidden",
        "@sm": { gridCols: 2 },
        "@lg": { gridCols: 4 },
      }}
    >
      <AtAGlanceLinkItem
        icon={<UserIcon />}
        value={formatWholeNumber(totalCurrentEmployees)}
        label="Staff members"
        linkText="Manage staff"
        from="/supporter/$supporterId/"
        to="./team"
      />
      <AtAGlanceLinkItem
        icon={<BagDollarIcon />}
        value={formatWholeNumber(Math.max(totalCurrentEmployees - unsupportedMembers.length, 0))}
        label="Staff with allowances"
        linkText="Manage allowances"
        from="/supporter/$supporterId/"
        to="./allowances"
      />
      {eapData && isAnyCountryEapEnabled(eapData) ? (
        <AtAGlanceLinkItem
          icon={<HealthcareIcon />}
          value={formatWholeNumber(totalCurrentEmployees)}
          label="Staff with EAP"
          linkText="Manage EAP"
          from="/supporter/$supporterId/"
          to="./eap/settings"
        />
      ) : (
        <AtAGlanceItem
          icon={<HealthcareIcon />}
          value={formatWholeNumber(0)}
          label="Staff with EAP"
          linkText="Enquire about EAP"
          href={`mailto:hello@givenwell.com?subject=EAP%20enquiry%20for%20${supporter.name}`}
        />
      )}
      <AtAGlanceLinkItem
        icon={<CreditCardIcon />}
        value={`${format(wallet.balance, currency).amount} ${format(wallet.balance, currency).currency}`}
        label="Wallet balance"
        linkText="View wallet"
        from="/supporter/$supporterId/"
        to="./financial-reports/transactions"
      />
    </Box>
  );
}

const AtAGlanceItem = function AtAGlanceItem({
  value,
  icon,
  label,
  linkText,
  ...props
}: ComponentProps<"a"> & {
  icon: ReactNode;
  value: string;
  label: string;
  linkText: string;
}) {
  return (
    <Flex
      as="a"
      {...props}
      css={{
        backgroundColor: colors.white,
        // border: `1px solid ${colors.outline2}`,
        // borderRadius: 6 + 16,
        padding: 20,
        // paddingRight: 32,
        flexDirection: "column",
      }}
    >
      <Flex css={{ alignItems: "flex-start", gap: 8 }}>
        {icon}
        <Box css={{ flex: "1 0 0px" }}>
          <Box css={{ spaceY: 2 }}>
            <Text css={{ fontScale: "2xl", lineHeight: 1, fontWeight: 600, fontFamily: fonts.ftHabit }}>{value}</Text>
            <Box css={{ fontScale: "sm", lineHeight: 1 }}>{label}</Box>
          </Box>
          {/* <Separator css={{ my: 12 }} /> */}
          <Box css={{ height: 24 }} />
          <Flex css={{ items: "center", gap: 0 }}>
            <Box
              css={{
                // ml: "auto",
                fontSize: 15,
                lineHeight: 1,
                fontWeight: 500,
                fontFamily: fonts.ftHabit,
                "a:hover &": { textDecoration: "underline" },
              }}
            >
              {linkText}
            </Box>
            <CaretRightIcon />
          </Flex>
        </Box>
      </Flex>
    </Flex>
  );
};

const AtAGlanceLinkItem = createLink(AtAGlanceItem);

type TodoType = "getting-started" | "urgent" | "admin" | "engagement";

type TodoListItem = {
  /** Stable — dismissed ids are persisted in supporter settings, so don't rename them casually. */
  id: string;
  isCompleted?: boolean;
  type?: TodoType;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /**
   * Set on todos that aren't relevant to every supporter (e.g. "set a wallet buffer"). Adds a
   * dismiss button, and the todo stays hidden once its id is stored in `dismissedTodos`.
   */
  isDismissible?: boolean;
};

/** Urgent items first, then anything that blocks getting set up, then the routine nudges. */
const todoTypeOrder: Record<TodoType, number> = {
  urgent: 0,
  "getting-started": 1,
  admin: 2,
  engagement: 3,
};

/** Returning `undefined` from `fn` marks the todo completed, so it's filtered out of the list. */
function createTodoItem(id: string, fn: () => Omit<TodoListItem, "id"> | undefined): TodoListItem {
  return {
    id,
    ...(fn() ?? {
      isCompleted: true,
      title: "",
    }),
  };
}

const DISMISSED_TODOS_SETTING = "dismissedTodos";

function useTodoList() {
  const { supporterId } = Route.useParams();

  // The dashboard's currency selector is optional in the URL, so fall back to the supporter's home
  // currency — otherwise no wallet matches and balances read as 0.
  let { currency } = Route.useSearch();
  const { data: supporter } = useSuspenseQuery(supporterQuery(supporterId));
  currency = currency ?? countryCodeToTokenCurrencyCode(supporter.country_code)!;

  const { data: invoices } = useQuery(supporterInvoicesQuery(supporterId, 1, 50));
  const legalTermsQuery = useQuery(supporterLegalTermsListQuery(supporterId));
  const { data: staff } = useSuspenseQuery(membersQuery(supporterId));
  const benefits = useSupporterBenefits(supporterId);
  const { data: unsupportedMembers } = useSuspenseQuery(unsupportedMembersQuery(supporterId));
  const { data: wallets } = useSuspenseQuery(supporterWalletsQuery(supporterId));
  const wallet = wallets.wallets.find(w => w.currency === currency);
  const { data: eapSessionRequests } = useQuery(
    allEapSessionRequestsQuery({
      supporterId,
      status: "pending",
      page: 1,
      pageSize: 1,
    }),
  );

  // `shortfallForecasts` is the backend's own "this batch won't have enough funds" forecast. Paged
  // the same way as the Allowances page (which shares this cache entry), since it's unclear whether
  // the forecast covers more than the returned page of rules.
  const { data: allowanceRules } = useQuery(allowanceRulesQuery({ supporterId, page: 1, pageSize: 2000 }));
  const { data: billingDetails } = useQuery(supporterBillingDetailsQuery(supporterId));
  const { data: registeredCountries } = useQuery(supporterCountriesQuery(supporterId));
  const { data: staffUsers } = useQuery(supporterStaffQuery(supporterId));
  const { data: eapSettings } = useQuery(supporterEAPSettingsQuery(supporterId));
  const { data: dismissedSetting } = useQuery(supporterSettingQuery(supporterId, DISMISSED_TODOS_SETTING));
  const setSupporterSetting = useSetSupporterSettingMutation(supporterId);
  const dismissedIds = Array.isArray(dismissedSetting) ? (dismissedSetting as string[]) : [];

  const addStaffTodo = createTodoItem("add-staff", () => {
    if (staff.members.length) return;

    return {
      title: "Add Staff",
      type: "getting-started",
      description: "Add the staff you want to give benefits to — everything else starts from here.",
      actions: (
        <>
          <AddTeamMemberModal>
            <TodoButton>Add staff</TodoButton>
          </AddTeamMemberModal>
        </>
      ),
    };
  });

  const createAllowanceTodo = createTodoItem("create-allowance", () => {
    // EAP-only supporters fund sessions per request, so they never need a recurring allowance.
    if (benefits.eap) return;
    // Wait for the query rather than flashing the todo at supporters who already have rules.
    if (!allowanceRules || allowanceRules.allowances.length > 0) return;

    return {
      type: "getting-started",
      title: "Create your first allowance",
      description: "Set up a recurring allowance so your staff have points to spend.",
      actions: (
        <CreateAllowanceRuleModal
          walletBalance={wallet?.balance || 0}
          defaultValues={{ supporter_member_ids: unsupportedMembers.map(mem => mem.supporter_member_id) }}
        >
          <TodoButton>Create allowance</TodoButton>
        </CreateAllowanceRuleModal>
      ),
    };
  });

  const billingDetailsTodo = createTodoItem("billing-details", () => {
    if (!billingDetails) return;
    if (billingDetails.email && billingDetails.address && billingDetails.timezone) return;

    return {
      type: "getting-started",
      title: "Complete your billing details",
      description: "These are used when we generate your invoices.",
      actions: (
        <TodoButtonLink to="/supporter/$supporterId/settings/billing" params={{ supporterId }}>
          Go to billing
        </TodoButtonLink>
      ),
    };
  });

  const eapCategoriesTodo = createTodoItem("eap-categories", () => {
    if (!benefits.eap || !eapSettings || eapSettings.selected_categories.length > 0) return;

    return {
      type: "getting-started",
      title: "Choose your EAP categories",
      description: "Pick the kinds of support your staff can book EAP sessions for.",
      actions: (
        <TodoButtonLink to="/supporter/$supporterId/eap/settings" params={{ supporterId }}>
          Choose categories
        </TodoButtonLink>
      ),
    };
  });

  const inviteAdminTodo = createTodoItem("invite-admin", () => {
    if (!staffUsers || staffUsers.length > 1) return;

    return {
      type: "getting-started",
      isDismissible: true,
      title: "Invite another admin",
      description: "Add a colleague so you aren’t the only person who can manage your account.",
      actions: (
        <TodoButtonLink to="/supporter/$supporterId/admins" params={{ supporterId }}>
          Invite admin
        </TodoButtonLink>
      ),
    };
  });

  const registerCountriesTodo = createTodoItem("register-countries", () => {
    // A supporter always has their home country registered, so one row means "home only".
    if (!registeredCountries || registeredCountries.length > 1) return;

    return {
      type: "getting-started",
      isDismissible: true,
      title: "Add the countries your staff are based in",
      description: "Register a country to give staff there allowances in their local currency.",
      actions: (
        <TodoButtonLink to="/supporter/$supporterId/settings/countries" params={{ supporterId }}>
          Add countries
        </TodoButtonLink>
      ),
    };
  });

  const walletBufferTodo = createTodoItem("wallet-buffer", () => {
    // Only worth suggesting once allowances exist — a buffer sits on top of recurring allowances.
    if (!allowanceRules?.allowances.length) return;
    if (!wallet || wallet.buffer_tokens > 0) return;

    return {
      type: "admin",
      isDismissible: true,
      title: "Set a wallet buffer",
      description: "A buffer keeps a minimum extra balance in your wallet, ready for one-off allowances or rewards.",
      actions: (
        <TodoButtonLink to="/supporter/$supporterId/settings/billing" params={{ supporterId }}>
          Set a buffer
        </TodoButtonLink>
      ),
    };
  });

  const shortfallTodo = createTodoItem("allowance-shortfall", () => {
    const shortfalls = allowanceRules?.shortfallForecasts ?? [];
    const nextShortfall = [...shortfalls].sort((a, b) => a.batch_date.localeCompare(b.batch_date))[0];
    if (!nextShortfall) return;

    const shortfallAmount = formatDollars(
      nextShortfall.batch_short_fall_amount,
      nextShortfall.batch_short_fall_currency.toUpperCase(),
    );

    return {
      type: "urgent",
      title: "Top up before your next allowance run",
      description: (
        <>
          Allowances on {formatDate(nextShortfall.batch_date)} are predicted to be short{" "}
          <strong>{shortfallAmount}</strong>.
        </>
      ),
      actions: (
        <TodoButtonLink to="/supporter/$supporterId/allowances" params={{ supporterId }}>
          View allowances
        </TodoButtonLink>
      ),
    };
  });

  const notActivatedTodo = createTodoItem("staff-not-activated", () => {
    // Aggregate counts rather than per-member status, which supporters can't see.
    const notSignedUpCount = staff.invited_count - staff.activated_count;
    if (notSignedUpCount <= 0) return;

    return {
      type: "engagement",
      isDismissible: true,
      title: `${formatWholeNumber(notSignedUpCount)} ${notSignedUpCount === 1 ? "staff member hasn’t" : "staff members haven’t"} logged in yet`,
      description: "Remind them to accept their invitation so they can start using their benefits.",
      actions: (
        <TodoButtonLink to="/supporter/$supporterId/team" params={{ supporterId }}>
          Go to staff
        </TodoButtonLink>
      ),
    };
  });

  const topUpRequestsTodo = createTodoItem("eap-top-up-requests", () => {
    if (!benefits.eap || !eapSessionRequests?.total_items) return;

    return {
      type: "urgent",
      title: `Review ${eapSessionRequests.total_items} EAP top-up ${eapSessionRequests.total_items === 1 ? "request" : "requests"}`,
      actions: (
        <>
          <TodoButtonLink to="/supporter/$supporterId/eap/requests" params={{ supporterId }}>
            Review requests
          </TodoButtonLink>
        </>
      ),
    };
  });

  const addAllowancesTodo = createTodoItem("add-allowances", () => {
    const totalCurrentEmployees = staff?.members.length;

    if (!unsupportedMembers.length) return;
    // EAP-only supporters don't use recurring allowances at all.
    if (benefits.eap && totalCurrentEmployees === unsupportedMembers.length) return;
    // Before the first rule exists this is the "create your first allowance" todo instead.
    if (!allowanceRules?.allowances.length) return;

    return {
      type: "admin",
      title: `Add allowances for ${formatWholeNumber(unsupportedMembers.length)} new staff`,
      actions: (
        <>
          <CreateAllowanceRuleModal
            walletBalance={wallet?.balance || 0}
            defaultValues={{ supporter_member_ids: unsupportedMembers.map(mem => mem.supporter_member_id) }}
          >
            <TodoButton>Add allowances</TodoButton>
          </CreateAllowanceRuleModal>
        </>
      ),
    };
  });

  const invoiceTodo = createTodoItem("pay-invoice", () => {
    // Invoices are intentionally not filtered by the selected currency — an unpaid invoice in
    // another currency still stops that country's allowances from going out.
    const firstUnpaidInvoice = invoices?.items.find(i => i.total_amount !== i.paid_amount);
    if (!firstUnpaidInvoice) return;

    const millisLeft = new Date(firstUnpaidInvoice.due_date).getTime() - new Date().getTime();
    const isOverdue = millisLeft < 0;

    const invoiceValueString = `${new Intl.NumberFormat("en-NZ", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(firstUnpaidInvoice.total_amount)} ${firstUnpaidInvoice.total_currency_code.toUpperCase()}`;

    const hasAllowances = allowanceRules && allowanceRules.allowances.length > 0;

    return {
      type: isOverdue ? "urgent" : "admin",
      title: isOverdue ? "Your invoice is overdue" : "You have an invoice coming up",
      description: (
        <>
          You have an invoice for {invoiceValueString}{" "}
          {isOverdue ? (
            <>
              that was due <strong>{formatRelativeTime(millisLeft)}</strong>.{" "}
              {hasAllowances ? "If payment isn’t received, your scheduled allowances won’t go out on time." : ""}
            </>
          ) : (
            <>
              due <strong>{formatRelativeTime(millisLeft)}</strong>.{" "}
              {hasAllowances ? "Paying on time makes sure your allowances will go out as scheduled." : ""}
            </>
          )}
        </>
      ),
      actions: (
        <>
          <TodoButton
            onClick={() => {
              window.open(firstUnpaidInvoice.download_url, "_blank")?.focus();
            }}
          >
            View invoice
          </TodoButton>
        </>
      ),
    };
  });

  const termsTodo = createTodoItem("accept-terms", () => {
    const pendingTerms = (legalTermsQuery.data ?? []).filter(doc => doc.state === "active" && !doc.is_accepted);
    if (!pendingTerms.length) return;

    return {
      type: "urgent",
      title: "We've updated our Terms of Service.",
      actions: (
        <>
          <TodoButtonLink to="/supporter/$supporterId/settings/givenwell-terms" params={{ supporterId: supporterId }}>
            Review terms
          </TodoButtonLink>
        </>
      ),
    };
  });

  // Declaration order only breaks ties within a type — `todoTypeOrder` decides the grouping.
  const todos: TodoListItem[] = [
    // Getting started
    addStaffTodo,
    createAllowanceTodo,
    eapCategoriesTodo,
    billingDetailsTodo,
    // inviteAdminTodo,
    // registerCountriesTodo,

    // Urgent
    invoiceTodo,
    shortfallTodo,
    // termsTodo,
    topUpRequestsTodo,

    // Admin
    addAllowancesTodo,
    walletBufferTodo,

    // Engagement
    // notActivatedTodo,
  ]
    .filter(todo => !todo.isCompleted)
    .filter(todo => !todo.isDismissible || !dismissedIds.includes(todo.id))
    // Array.prototype.sort is stable, so todos of the same type keep their declared order.
    .sort((a, b) => (a.type ? todoTypeOrder[a.type] : Infinity) - (b.type ? todoTypeOrder[b.type] : Infinity));

  function dismiss(id: string) {
    setSupporterSetting.mutate({
      key: DISMISSED_TODOS_SETTING,
      value: [...dismissedIds, id],
    });
  }

  return { todos, dismiss, isDismissing: setSupporterSetting.isPending };
}

function TodoList() {
  const { todos, dismiss, isDismissing } = useTodoList();

  return (
    <RichCard title="Your To-dos">
      {todos.length ? (
        <Box
          css={{
            "& > * + *": {
              borderTop: `1px solid ${colors.outline2}`,
            },
          }}
        >
          {todos.map(todo => {
            // Urgent todos always sort to the top, so the tinted rows read as one block. The accent bar
            // is an inset shadow rather than a border so it doesn't shift the row's padding.
            const isUrgent = todo.type === "urgent";

            return (
              <Flex
                key={todo.id}
                css={{ items: "flex-start", paddingInline: 20, paddingBlock: 20 }}
                style={
                  isUrgent
                    ? { backgroundColor: colors.pinkLight, boxShadow: `inset 3px 0 0 ${colors.pinkVivid}` }
                    : undefined
                }
              >
                <Box css={{ width: 110, lineHeight: 0 }}>
                  <TodoStatusBadge status={todo.type} />
                </Box>
                <Box css={{ flex: "1 0 0px" }}>
                  <Box css={{ fontScale: "sm", lineHeight: "20px" }} style={{ fontWeight: isUrgent ? 600 : 500 }}>
                    {todo.title}
                  </Box>
                  <Box css={{ fontScale: "sm", color: colors.text2 }}>{todo.description}</Box>
                </Box>
                <Flex css={{ flexDirection: "column", width: 150 }}>
                  {todo.actions}
                  {todo.isDismissible && (
                    <TodoButton variant="plain" disabled={isDismissing} onClick={() => dismiss(todo.id)}>
                      I donʼt need this
                    </TodoButton>
                  )}
                </Flex>
              </Flex>
            );
          })}
        </Box>
      ) : (
        <EmptyState
          icon={<CheckCircleIcon width={14 * 3} height={14 * 3} />}
          title="All done!"
          description="Nothing needs your attention right now — anything new will show up here."
        />
      )}
    </RichCard>
  );
}

const TodoButton = styled("button", {
  height: 32,
  borderRadius: 6,
  color: colors.text,
  fontScale: "sm",
  fontFamily: fonts.ftHabit,

  variants: {
    variant: {
      filled: {
        backgroundColor: colors.cyan,
      },
      plain: {
        backgroundColor: "transparent",
        color: colors.text2,
      },
    },
  },

  defaultVariants: {
    variant: "filled",
  },
});

const TodoButtonLink = createLink(TodoButton);

function TodoStatusBadge({ status }: { status: TodoType | undefined }) {
  if (!status) {
    return null;
  }
  const color = {
    "getting-started": colors.cyanAccent,
    admin: colors.cyanAccent,
    engagement: colors.yellowAccent,
    urgent: colors.pinkAccent,
  }[status];
  const name = {
    "getting-started": "Get Started",
    admin: "Admin",
    engagement: "Engagement",
    urgent: "Urgent",
  }[status];

  return (
    <Span
      css={{
        height: 20,
        display: "inline-flex",
        alignItems: "center",
        borderRadius: 999,
        px: 8,
        fontWeight: 500,
        fontScale: "xs",
      }}
      style={{ backgroundColor: color }}
    >
      {name}
    </Span>
  );
}
